/**
 * <TopBar>
 *
 * Sits above <main> inside the main column. Because it's a normal-flow
 * flex row (not sticky/absolute), it can never block content clicks and
 * never gets its own z-index war.
 *
 * Rules:
 *   • Every icon button has a 44×44 minimum hit target.
 *   • The search input has explicit `flex-1 min-w-0` so it never overflows
 *     the row on narrow screens.
 *   • Icon buttons are wrapped in <motion.button> for spring hover/tap.
 *   • No absolute-positioned overlays inside the bar — future dropdowns
 *     will use Portals to escape.
 */

import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Moon, Search, Settings as SettingsIcon, Sun, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { initials } from '@homejournal/utils';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/lib/theme';
import { signOut } from '@/lib/supabase';
import { env } from '@/lib/env';
import { useSearch } from '@/lib/search';
import { iconButtonGesture } from '@/components/common/motion-variants';

/** Routes that surface the global search input. Everything else shows the brand header. */
const SEARCH_ROUTES = new Set<string>(['/expenses']);

/**
 * The wordmark shown on every non-search route. Kept lightweight — a small
 * emblem, the family name in serif, and a tiny sub-line. `min-w-0 flex-1`
 * matches the search block's flex behavior so the row stays balanced.
 */
function BrandHeader() {
  return (
    <div className="flex min-w-0 flex-1 flex-col justify-center">
      <h1
        className={[
          'truncate font-serif text-lg leading-tight tracking-tight',
          'text-earth-900 dark:text-mist-50',
          'sm:text-xl md:text-2xl',
        ].join(' ')}
      >
        PSK Illam Journal
      </h1>
      <p
        className={[
          'hidden truncate text-[11px] font-medium uppercase tracking-[0.18em]',
          'text-earth-500 dark:text-mist-400',
          'sm:block',
        ].join(' ')}
      >
        A home, one page at a time
      </p>
    </div>
  );
}

export function TopBar() {
  const { session } = useAuth();
  const { resolved, setTheme } = useTheme();
  const { term, setTerm, clear } = useSearch();
  const location = useLocation();
  const showSearch = SEARCH_ROUTES.has(location.pathname);

  // Route change → search input hidden → drop any lingering filter so the next
  // page doesn't inherit an invisible term.
  useEffect(() => {
    if (!showSearch && term) clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSearch]);

  // Prefer full_name, fall back to email — for initials + a11y label.
  const displayName =
    (session?.user?.user_metadata?.full_name as string | undefined) ??
    session?.user?.email ??
    (env.devBypassAuth ? 'Dev User' : '');

  return (
    <header
      className={[
        // Shrink to intrinsic height; never grow past it.
        'shrink-0',
        // Row of controls with sensible gaps.
        'flex items-center gap-2 md:gap-3',
        // Comfortable padding; safe-area on top for iOS notch devices.
        'safe-t px-4 py-3 md:px-8 md:py-5',
        // Subtle bottom border on desktop; mobile has BottomNav for divider.
        'md:border-earth-100 md:border-b md:dark:border-white/[0.06]',
      ].join(' ')}
    >
      {/*
        LEADING SLOT — either the global search (Expenses) or the brand header
        (everywhere else). Both use `min-w-0 flex-1` so the row stays balanced.
      */}
      {showSearch ? (
        <div
          className={[
            'flex min-w-0 flex-1 items-center gap-2',
            'border-earth-100 rounded-full border bg-white/70 px-3 py-2',
            'backdrop-blur-xl',
            'dark:bg-earth-900/50 dark:border-white/[0.06]',
            'focus-within:ring-brass-400 focus-within:ring-2',
          ].join(' ')}
        >
          <Search className="text-earth-400 h-4 w-4 shrink-0" aria-hidden="true" />
          <input
            type="search"
            placeholder="Search expenses…"
            aria-label="Search expenses"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            className={[
              'min-w-0 flex-1 bg-transparent text-sm',
              'text-earth-900 placeholder:text-earth-400',
              'focus:outline-none',
              'dark:text-earth-50',
            ].join(' ')}
          />
          {term ? (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear search"
              className="text-earth-500 hover:bg-earth-100 hover:text-earth-800 dark:hover:text-earth-100 grid h-6 w-6 shrink-0 place-items-center rounded-full dark:hover:bg-white/10"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : (
            <kbd
              aria-hidden="true"
              className={[
                'hidden shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
                'bg-earth-100 text-earth-500 lg:inline',
                'dark:text-earth-300 dark:bg-white/[0.06]',
              ].join(' ')}
            >
              ⌘K
            </kbd>
          )}
        </div>
      ) : (
        <BrandHeader />
      )}

      {/*
        THEME TOGGLE — full 44×44 tap area, motion-wrapped so the whole
        button reacts, not just the icon.
      */}
      <motion.button
        {...iconButtonGesture}
        type="button"
        onClick={() => setTheme(resolved === 'dark' ? 'light' : 'dark')}
        aria-label={resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        className={[
          // 44×44 hit target.
          'inline-flex h-11 w-11 shrink-0 items-center justify-center',
          'rounded-full',
          'text-earth-700 hover:bg-earth-100',
          'dark:text-earth-200 dark:hover:bg-white/[0.06]',
          'transition-colors',
          'focus-visible:ring-brass-400 focus-visible:outline-none focus-visible:ring-2',
        ].join(' ')}
      >
        {resolved === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </motion.button>

      <motion.div {...iconButtonGesture}>
        <Link
          to="/settings"
          aria-label="Settings"
          className={[
            'inline-flex h-11 w-11 shrink-0 items-center justify-center',
            'rounded-full',
            'text-earth-700 hover:bg-earth-100',
            'dark:text-earth-200 dark:hover:bg-white/[0.06]',
            'transition-colors',
            'focus-visible:ring-brass-400 focus-visible:outline-none focus-visible:ring-2',
          ].join(' ')}
        >
          <SettingsIcon className="h-4 w-4" />
        </Link>
      </motion.div>

      {/* SIGN OUT — only when a real session exists (not in dev bypass). */}
      {!env.devBypassAuth && session && (
        <motion.button
          {...iconButtonGesture}
          type="button"
          onClick={() => void signOut()}
          aria-label="Sign out"
          className={[
            'inline-flex h-11 w-11 shrink-0 items-center justify-center',
            'rounded-full',
            'text-earth-700 hover:bg-earth-100',
            'dark:text-earth-200 dark:hover:bg-white/[0.06]',
            'transition-colors',
            'focus-visible:ring-brass-400 focus-visible:outline-none focus-visible:ring-2',
          ].join(' ')}
        >
          <LogOut className="h-4 w-4" />
        </motion.button>
      )}

      {/*
        USER AVATAR — decorative; hidden on tiny screens where the sign-out
        button is enough. Becomes a button when we add a dropdown menu.
      */}
      {displayName && (
        <div
          aria-label={`Signed in as ${displayName}`}
          className={[
            'hidden h-11 w-11 shrink-0 place-items-center rounded-full',
            'bg-earth-800 text-earth-50 font-serif text-sm',
            'sm:grid',
            'dark:bg-earth-100 dark:text-earth-900',
          ].join(' ')}
        >
          {initials(displayName) || '?'}
        </div>
      )}
    </header>
  );
}
